import { Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { DESKTOP_DEVICE, MOBILE_DEVICE, ScreenService } from '../../core/screen/screen.service';
import { UserService } from '../../core/user/user.service';
import { TitleService } from '../../core/title/title.service';
import { RefreshService } from '../../core/refresh/refresh.service';
import { ProgressService } from '../../core/progress/progress.service';
import { delay, filter, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AnchorIconButton, TopToolbarIconBotton } from './navigation-data';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AppPageComponent, getAppPageConfiguration, PageConfiguration } from '../component-base/app-page.component';
import { clone, defaults } from 'lodash';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { AccountComponent } from '../account/account.component';
import { MatProgressBar } from '@angular/material/progress-bar';
import { Optional } from '../shared.types';

const DEFAULT_PAGE_CONFIG: PageConfiguration = {
  showMobileBottomNavbar: true,
  showMobilePageTitle: false,
  hideDesktopAccountMenu: false,
  topLeftToolbarIconButton: null,
  topRightToolbarIconButton: null,
  useAppFullWidth: false,
  hideDesktopNavigationMenu: false,
  hideHeaderToolbar: false,
  useDesktopAppFullWidth: false
};

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  imports: [
    MatIconButton,
    MatIcon,
    MatSidenavContainer,
    MatSidenavContent,
    MatToolbar,
    NgTemplateOutlet,
    MatButton,
    RouterLink,
    RouterLinkActive,
    NgClass,
    MatMenuTrigger,
    MatMenu,
    AccountComponent,
    MatProgressBar,
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {
  readonly refresh = inject(RefreshService);
  readonly screen = inject(ScreenService);
  readonly user = inject(UserService);
  readonly title = inject(TitleService);
  private progress = inject(ProgressService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  readonly navItems = input<NavItem[]>();

  readonly showMobileBottomNavbar = computed(() => {
    if (this.screen.device !== MOBILE_DEVICE) {
      return false;
    }
    return this.pageConfiguration().showMobileBottomNavbar ?? true;
  });

  readonly inProgress = signal(false);
  readonly pageConfiguration = signal<PageConfiguration>(DEFAULT_PAGE_CONFIG);

  private subscription?: Subscription;
  private currentPage: any;
  private pageConfigurationChangesSubscription?: Subscription;

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(map(() => this.activatedRoute))
      .pipe(
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
      )
      .subscribe((value) => {
        if (value && value.component) {
          const pageConfig = getAppPageConfiguration(value.component) || {};
          const newConfig = defaults(
            clone(pageConfig),
            DEFAULT_PAGE_CONFIG,
          );
          this.pageConfiguration.set(newConfig);
        }
      });
  }

  ngOnInit() {
    this.subscription = this.progress
      .getProgress()
      // Workaround für WebKit damit er genug Zeit für das Redraw hat
      .pipe(delay(50))
      .subscribe((active) => {
        this.inProgress.set(active);
      });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  onRouterOutletActivate(event: any) {
    if (this.pageConfigurationChangesSubscription) {
      this.pageConfigurationChangesSubscription.unsubscribe();
      this.pageConfigurationChangesSubscription = undefined;
    }
    this.currentPage = event;
    if (this.currentPage instanceof AppPageComponent) {
      this.pageConfigurationChangesSubscription =
        this.currentPage.pageConfigurationChanges.subscribe((pageConfig) => {
          const currentConfig = this.pageConfiguration()
          this.pageConfiguration.set(defaults(pageConfig, clone(currentConfig)));
        });
    }
  }

  isShowTopLeftToolbarIconButton(device: string): boolean {
    return this.isShowToolbarIconButton(
      device,
      this.pageConfiguration()?.topLeftToolbarIconButton,
    );
  }

  isShowTopRightToolbarIconButton(device: string): boolean {
    return this.isShowToolbarIconButton(
      device,
      this.pageConfiguration()?.topRightToolbarIconButton,
    );
  }

  executeAction(actionName: string, state?: any, event?: any) {
    if (this.currentPage && this.currentPage instanceof AppPageComponent) {
      this.currentPage.executeAction(actionName, state, event);
    }
  }

  private isShowToolbarIconButton(
    device: string,
    button: Optional<TopToolbarIconBotton>,
  ): boolean {
    return !!(
      button &&
      ((device == DESKTOP_DEVICE && button.showOnDesktopDevice) ||
        (device == MOBILE_DEVICE && button.showOnMobileDevice))
    );
  }


}

export type NavItem = AnchorIconButton;
